import React, { useRef, useMemo } from "react";
import PropTypes from "prop-types";

import Header from "layout/discovery/folder/categories/Header";
import { useSelector } from "react-redux";
import InfiniteGrid from "components/Grid";
import { filterThemes, getIsCycle } from "utils/discovery";
import { hoverSame, unhoverSame } from "utils";
import Category from "layout/discovery/Category";
import Item from "layout/discovery/Item";
import { sortCategoryList } from "layout/discovery/helpers";

const Categories = ({ categoriesRef, documentsRef, folderView = true }) => {
  const outerListRef = useRef(undefined);
  const innerListRef = useRef(undefined);
  const isCycle = useMemo(getIsCycle, []);

  const allCategories = useSelector((state) => state.discovery.themes);
  const categories = allCategories.filter((t) => t.GroupName !== "All Themes");
  const showAll = useSelector((state) => state.discovery.config.showAll);
  const isSearch = useSelector((state) => state.discovery.config.isSearch);
  const filterView = useSelector((state) => state.discovery.config.filterView);

  let categoryList = filterThemes(categories, showAll, isSearch, filterView);
  categoryList = sortCategoryList(categoryList, isSearch, filterView);

  // using Array.reduce method to avoid stack overflow exceptions when using Math library on large arrays
  const maxOccurrences = categoryList?.reduce(
    (max, current) => Math.max(max, current.Occurrences),
    0
  );

  const renderRow = ({ rowIndex, style }) => {
    const checked =
      categoryList[rowIndex].checked !== undefined
        ? categoryList[rowIndex].checked
        : false;

    const isCollapsed =
      categoryList[rowIndex + 1] === undefined ||
      categoryList[rowIndex + 1].NounPhrase === undefined;

    return (
      <div
        style={style}
        className={`item item-${rowIndex}`}
        data-testid="categories-item"
        onMouseOver={() => hoverSame(rowIndex)}
        onFocus={() => hoverSame(rowIndex)}
        onMouseOut={unhoverSame}
        onBlur={unhoverSame}
      >
        {categoryList[rowIndex].NounPhrase ? (
          <Item
            item={categoryList[rowIndex]}
            checked={checked}
            maxOccurrences={categoryList[0].Occurrences}
          />
        ) : (
          <Category
            category={categoryList[rowIndex]}
            categories={categories}
            isCollapsed={isCollapsed}
            maxOccurrences={maxOccurrences}
            alignCollapseButtonToRight
          />
        )}
      </div>
    );
  };

  const handleScroll = (scrollTop) => {
    if (documentsRef.current && !isCycle(scrollTop)) {
      documentsRef.current.scrollTo({ scrollTop });
    }
  };

  return (
    <div className="categories" data-testid="categories-pane">
      <Header folderView={folderView} />
      <div className="categories__body" data-testid="categories-body">
        <InfiniteGrid
          listRef={categoriesRef}
          rowSize={26}
          outerRef={outerListRef}
          innerRef={innerListRef}
          rowCount={categoryList.length}
          onScroll={(_, scrollTop) => handleScroll(scrollTop)}
          renderRow={renderRow}
          columnCount={1}
          hiddenScrollbar={folderView}
        />
      </div>
    </div>
  );
};

export default Categories;

Categories.propTypes = {
  categoriesRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.object,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]).isRequired,
  documentsRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.object,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]).isRequired,
  folderView: PropTypes.bool,
};
